import React, { useEffect, useState, useCallback } from 'react';
import { ncmRates, importSettings } from '../utils/api';
import { useT } from '../i18n/I18nContext';
import { useToast, ToastContainer } from '../hooks/useToast';

const SETTINGS_EMPTY = {
  default_icms_pct: 0,
  default_afrmm_pct: 0,
  siscomex_fixed_brl: 0,
  ipi_recuperavel: false,
  pis_recuperavel: false,
  cofins_recuperavel: false,
  icms_recuperavel: false,
};

const NCM_EMPTY = {
  ncm: '',
  description: '',
  ii_pct: '',
  ipi_pct: '',
  pis_pct: '',
  cofins_pct: '',
};

export default function ImportConfig() {
  const t = useT();
  const { toasts, toast } = useToast();

  // --- Import settings state ---
  const [settings, setSettings] = useState(SETTINGS_EMPTY);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // --- NCM table state ---
  const [ncms, setNcms] = useState([]);
  const [ncmLoading, setNcmLoading] = useState(true);
  const [editingNcm, setEditingNcm] = useState(null); // ncm code string or null
  const [editRow, setEditRow] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(NCM_EMPTY);
  const [addSaving, setAddSaving] = useState(false);

  // --- Load settings ---
  const loadSettings = useCallback(() => {
    setSettingsLoading(true);
    importSettings.get()
      .then(data => setSettings(data || SETTINGS_EMPTY))
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
  }, []);

  // --- Load NCMs ---
  const loadNcms = useCallback(() => {
    setNcmLoading(true);
    ncmRates.list()
      .then(data => setNcms(Array.isArray(data) ? data : []))
      .catch(() => toast(t('importcfg.ncm.error_load'), 'error'))
      .finally(() => setNcmLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    loadSettings();
    loadNcms();
  }, [loadSettings, loadNcms]);

  // --- Settings handlers ---
  function setSettingField(key, value) {
    setSettings(s => ({ ...s, [key]: value }));
  }

  async function handleSaveSettings() {
    setSettingsSaving(true);
    try {
      await importSettings.update(settings);
      toast(t('importcfg.settings.saved'), 'success');
    } catch {
      toast(t('importcfg.settings.error'), 'error');
    } finally {
      setSettingsSaving(false);
    }
  }

  // --- NCM inline edit handlers ---
  function startEdit(row) {
    setEditingNcm(row.ncm);
    setEditRow({ ...row });
  }

  function cancelEdit() {
    setEditingNcm(null);
    setEditRow({});
  }

  async function handleSaveEdit() {
    try {
      await ncmRates.update(editingNcm, editRow);
      toast(t('importcfg.ncm.saved'), 'success');
      setEditingNcm(null);
      setEditRow({});
      loadNcms();
    } catch {
      toast(t('importcfg.ncm.error_save'), 'error');
    }
  }

  async function handleDelete(ncm) {
    if (!window.confirm(t('importcfg.ncm.delete_confirm'))) return;
    try {
      await ncmRates.delete(ncm);
      toast(t('importcfg.ncm.deleted'), 'default');
      loadNcms();
    } catch {
      toast(t('importcfg.ncm.error_delete'), 'error');
    }
  }

  // --- Add NCM handlers ---
  function setAddField(key, value) {
    setAddForm(f => ({ ...f, [key]: value }));
  }

  async function handleAdd() {
    if (!addForm.ncm || !addForm.description) return;
    setAddSaving(true);
    try {
      await ncmRates.save(addForm);
      toast(t('importcfg.ncm.added'), 'success');
      setAddForm(NCM_EMPTY);
      setShowAddForm(false);
      loadNcms();
    } catch {
      toast(t('importcfg.ncm.error_save'), 'error');
    } finally {
      setAddSaving(false);
    }
  }

  const pctInput = (val, onChange) => (
    <input
      type="number"
      value={val}
      onChange={e => onChange(e.target.value)}
      style={{ width: 70, textAlign: 'right' }}
      step="0.01"
      min="0"
    />
  );

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">🧾 {t('importcfg.title')}</div>
          <div className="page-subtitle">{t('importcfg.subtitle')}</div>
        </div>
      </div>

      {/* Disclaimer banner */}
      <div style={{
        background: '#fff8e1',
        border: '1px solid #ffe082',
        borderRadius: 'var(--radius)',
        padding: '10px 16px',
        marginBottom: 20,
        fontSize: 13,
        color: '#795548',
        fontWeight: 500,
      }}>
        {t('importcfg.disclaimer')}
      </div>

      {/* ── Import Settings card ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="card-title">⚙️ {t('importcfg.settings.title')}</div>
        </div>
        <div className="card-body">
          {settingsLoading ? (
            <div style={{ color: 'var(--gray-400)', fontSize: 13 }}>{t('importcfg.ncm.loading')}</div>
          ) : (
            <>
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>{t('importcfg.settings.icms')}</label>
                  <input
                    type="number"
                    value={settings.default_icms_pct ?? 0}
                    onChange={e => setSettingField('default_icms_pct', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                    {t('importcfg.settings.icms_note')}
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('importcfg.settings.afrmm')}</label>
                  <input
                    type="number"
                    value={settings.default_afrmm_pct ?? 0}
                    onChange={e => setSettingField('default_afrmm_pct', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>{t('importcfg.settings.siscomex')}</label>
                  <input
                    type="number"
                    value={settings.siscomex_fixed_brl ?? 0}
                    onChange={e => setSettingField('siscomex_fixed_brl', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>
                  {t('importcfg.settings.recuperavel')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {[
                    ['ipi_recuperavel',    t('importcfg.settings.ipi_rec')],
                    ['pis_recuperavel',    t('importcfg.settings.pis_rec')],
                    ['cofins_recuperavel', t('importcfg.settings.cofins_rec')],
                    ['icms_recuperavel',   t('importcfg.settings.icms_rec')],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!settings[key]}
                        onChange={e => setSettingField(key, e.target.checked)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSaveSettings}
                disabled={settingsSaving}
              >
                {settingsSaving ? '...' : t('importcfg.settings.save')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── NCM Table card ── */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">📋 {t('importcfg.ncm.title')}</div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { setShowAddForm(s => !s); setAddForm(NCM_EMPTY); }}
          >
            + {t('importcfg.ncm.add_title')}
          </button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div style={{
            background: 'var(--gray-50)',
            borderBottom: '1px solid var(--gray-100)',
            padding: '14px 20px',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: 'var(--pantone)' }}>
              {t('importcfg.ncm.add_title')}
            </div>
            <div className="form-grid" style={{ marginBottom: 10 }}>
              <div className="form-group">
                <label>{t('importcfg.ncm.col.ncm')}</label>
                <input
                  value={addForm.ncm}
                  onChange={e => setAddField('ncm', e.target.value)}
                  placeholder="8462.21.90"
                />
              </div>
              <div className="form-group full">
                <label>{t('importcfg.ncm.col.description')}</label>
                <input
                  value={addForm.description}
                  onChange={e => setAddField('description', e.target.value)}
                  placeholder="Prensas hidráulicas para trabalhar metais..."
                />
              </div>
              <div className="form-group">
                <label>{t('importcfg.ncm.col.ii')}</label>
                {pctInput(addForm.ii_pct, v => setAddField('ii_pct', v))}
              </div>
              <div className="form-group">
                <label>{t('importcfg.ncm.col.ipi')}</label>
                {pctInput(addForm.ipi_pct, v => setAddField('ipi_pct', v))}
              </div>
              <div className="form-group">
                <label>{t('importcfg.ncm.col.pis')}</label>
                {pctInput(addForm.pis_pct, v => setAddField('pis_pct', v))}
              </div>
              <div className="form-group">
                <label>{t('importcfg.ncm.col.cofins')}</label>
                {pctInput(addForm.cofins_pct, v => setAddField('cofins_pct', v))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={addSaving || !addForm.ncm || !addForm.description}>
                {addSaving ? '...' : t('importcfg.ncm.add')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddForm(false)}>
                {t('importcfg.ncm.cancel')}
              </button>
            </div>
          </div>
        )}

        {ncmLoading ? (
          <div style={{ padding: '20px', color: 'var(--gray-400)', fontSize: 13 }}>
            {t('importcfg.ncm.loading')}
          </div>
        ) : ncms.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🧾</div>
            <div className="empty-title">{t('importcfg.ncm.empty')}</div>
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th>{t('importcfg.ncm.col.ncm')}</th>
                  <th>{t('importcfg.ncm.col.description')}</th>
                  <th style={{ textAlign: 'right' }}>{t('importcfg.ncm.col.ii')}</th>
                  <th style={{ textAlign: 'right' }}>{t('importcfg.ncm.col.ipi')}</th>
                  <th style={{ textAlign: 'right' }}>{t('importcfg.ncm.col.pis')}</th>
                  <th style={{ textAlign: 'right' }}>{t('importcfg.ncm.col.cofins')}</th>
                  <th>{t('importcfg.ncm.col.source')}</th>
                  <th>{t('importcfg.ncm.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {ncms.map(row => {
                  const isEditing = editingNcm === row.ncm;
                  return (
                    <tr key={row.ncm}>
                      <td className="bold">{row.ncm}</td>
                      <td>
                        {isEditing ? (
                          <input
                            value={editRow.description || ''}
                            onChange={e => setEditRow(r => ({ ...r, description: e.target.value }))}
                            style={{ width: '100%', minWidth: 180 }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>{row.description}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing
                          ? pctInput(editRow.ii_pct, v => setEditRow(r => ({ ...r, ii_pct: v })))
                          : <span>{row.ii_pct}%</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing
                          ? pctInput(editRow.ipi_pct, v => setEditRow(r => ({ ...r, ipi_pct: v })))
                          : <span>{row.ipi_pct}%</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing
                          ? pctInput(editRow.pis_pct, v => setEditRow(r => ({ ...r, pis_pct: v })))
                          : <span>{row.pis_pct}%</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {isEditing
                          ? pctInput(editRow.cofins_pct, v => setEditRow(r => ({ ...r, cofins_pct: v })))
                          : <span>{row.cofins_pct}%</span>}
                      </td>
                      <td>
                        <span className="tag" style={{ fontSize: 11 }}>{row.source || '—'}</span>
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>
                              {t('importcfg.ncm.save')}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>
                              {t('importcfg.ncm.cancel')}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => startEdit(row)}>
                              ✏️
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(row.ncm)}>
                              🗑️
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
